import React, { FC, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Paper,
  IconButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import ApiIcon from '@mui/icons-material/Api';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import upi from 'src/components/logos/upi.png';
import imps from 'src/components/logos/imps.png';
import sepa from 'src/components/logos/sepa.png';
import fasterpayments from 'src/components/logos/fasterpayments.png';
import { motion } from 'framer-motion';

const codeSnippet = `import requests

url = "https://swiftramp.com/api/v1/wallet/conversion/fiat/sell"

headers = {
    "X-Timestamp": "{{timestamp}}",
    "X-Client-Id": "{{client_id}}",
    "X-Request-Id": "{{request_id}}",
    "X-User-Id": "{{user_id}}",
    "X-Secret-Key": "{{secret_key}}"
}

data = {
    "source_id": "{{id}}",
    "fiat_symbol": "INR",
    "symbol": "USDT",
    "amount": 1.14
}

response = requests.post(url, headers=headers, json=data`;

export const HomeFeatures: FC = () => {
  const theme = useTheme();
  
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(codeSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, rgba(255,107,0,0.08) 0%, rgba(255,107,0,0) 70%)',
          filter: 'blur(50px)',
          zIndex: 0
        }}
      />

      <Container maxWidth="lg"
sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 6, md: 12 },
            mb: 15
          }}
        >
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ flex: 1 }}
          >
            <Typography
              variant="h3"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: 'linear-gradient(90deg, #FF6B00 0%, #FF8A00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              A few lines of code
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                mb: 6,
                fontSize: '1.125rem',
                lineHeight: 1.6
              }}
            >
              Bringing payment to your business - Seamlessly integrate Web3 into your app with minimal code!
            </Typography>

            <Stack spacing={4}
sx={{ mb: 6 }}>
              {[
                {
                  icon: <CodeIcon />,
                  title: 'Web Integration',
                  description: 'Redirect to a web hosted SwiftRamp URL'
                },
                {
                  icon: <ApiIcon />,
                  title: 'API Integration',
                  description: 'Use our best in class APIs'
                }
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      display: 'flex',
                      gap: 3,
                      alignItems: 'flex-start',
                      p: 3,
                      backgroundColor: 'background.default',
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        '& .arrow-icon': {
                          opacity: 1,
                          transform: 'translateX(0)'
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'primary.main',
                        borderRadius: 1.5,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {React.cloneElement(item.icon, { sx: { color: 'white' } })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1"
sx={{ mb: 1, fontWeight: 600 }}>
                        {item.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {item.description}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon
                      className="arrow-icon"
                      sx={{
                        color: 'primary.main',
                        opacity: 0,
                        transform: 'translateX(-10px)',
                        transition: 'all 0.3s ease-in-out'
                      }}
                    />
                  </Paper>
                </motion.div>
              ))}
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
            >
              <Button
                variant="contained"
                href="https://cal.com/support-team-qsszsx"
                target="_blank"
                sx={{
                  py: 1.5,
                  px: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.3s ease-in-out'
                  },
                  '&:hover::after': {
                    transform: 'translateX(100%)'
                  }
                }}
              >
                Talk to us
              </Button>
              <Button
                variant="outlined"
                sx={{ py: 1.5, px: 3 }}
              >
                Read our Docs
              </Button>
            </Stack>
          </motion.div>

          {/* Right Column - Code Sample */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ flex: 1 }}
          >
            <Paper
              elevation={0}
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.800',
                borderRadius: 3,
                p: 3,
                color: 'common.white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: 'linear-gradient(90deg, #FF6B00, #FF8A00)'
                }
              }}
            >
              <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                <IconButton
                  size="small"
                  onClick={handleCopyCode}
                  sx={{
                    color: copied ? 'success.main' : 'grey.500',
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, 0.2)
                    }
                  }}
                >
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Box
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  overflow: 'auto',
                  maxHeight: '100%'
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {codeSnippet}
                </pre>
              </Box>
            </Paper>
          </motion.div>
        </Box>

        {/* Payment Methods */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Box sx={{ mb: 5 }}>
          <Typography
        variant="subtitle1"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          fontSize: '2rem',
          letterSpacing: 1,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Typography
        variant="subtitle1"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          fontSize: '1.5rem',
          letterSpacing: 1,
          mb: 5,
          textAlign: 'center',
        }}
      >
        Supported Payment Methods
      </Typography>

      </Typography>


            <Stack
              direction="row"
              spacing={6}
              justifyContent="center"
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{
                mx: 'auto',
                maxWidth: 800
              }}
            >
              {[upi, imps, sepa, fasterpayments].map((logo, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Payment Method"
                    sx={{
                      height: 32,
                      width: 'auto',
                      filter: 'grayscale(1)',
                      opacity: 0.8,
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        filter: 'grayscale(0)',
                        opacity: 1,
                        transform: 'scale(1.1)'
                      }
                    }}
                  />
                </motion.div>
              ))}
            </Stack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};
